// GINI Code -> GINI Studio handoff landing page (`/handoff/:code`).
//
// Flow, in the order a user experiences it:
//   probe the code (unauthenticated, payload-free)
//   -> if not signed in, show the sign-in gate and RESUME here afterwards
//   -> redeem once
//   -> show imported brief + source context + created/opened project
//   -> show generation availability truthfully (blocked stays blocked)
//   -> show artifact versions via bounded polling
//   -> produce a bounded return reference for GINI Code
//
// Resume-after-login works because the URL never changes: the path stays
// `/handoff/:code` through the whole sign-in detour, so re-rendering after
// authentication lands back on this component with the same code. Nothing is
// stashed in localStorage or a query parameter, which also means there is no
// place for the code to leak to.
//
// Redemption is guarded by a ref, not by state, so a re-render triggered by
// the auth transition cannot fire a second POST against a one-time code.

import { useEffect, useRef, useState } from 'react';
import { Button } from '@open-design/components';
import type { GiniHandoffSessionView } from '@open-design/contracts/gini-platform';

import { useT } from '../../i18n';
import { fetchAuthStatus, type AuthMeResponse } from '../../providers/auth';
import { probeHandoff, redeemHandoff } from '../../providers/gini-handoff';
import { Welcome } from '../Welcome';
import { ArtifactPanel } from './ArtifactPanel';
import { HostedAccountPanel } from './HostedAccountPanel';
import { ReturnPanel } from './ReturnPanel';
import { RunStatePanel } from './RunStatePanel';
import styles from './HandoffLanding.module.css';

type Phase =
  | { kind: 'checking' }
  | { kind: 'signin' }
  | { kind: 'redeeming' }
  | { kind: 'ready'; session: GiniHandoffSessionView }
  | { kind: 'unusable'; reason: 'expired' | 'consumed' | 'unknown' }
  | { kind: 'error'; message: string };

interface Props {
  code: string;
  onExit: () => void;
}

export function HandoffLanding({ code, onExit }: Props) {
  const t = useT();
  const [phase, setPhase] = useState<Phase>({ kind: 'checking' });
  const [auth, setAuth] = useState<AuthMeResponse | null>(null);
  const [selection, setSelection] = useState<{ artifactId: string; artifactVersion: number } | null>(null);
  const redeemed = useRef(false);
  const [authNonce, setAuthNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function begin(): Promise<void> {
      const status = await fetchAuthStatus();
      if (cancelled) return;
      setAuth(status);

      if (!status.authenticated) {
        // Probe first so an anonymous visitor holding a dead link is told
        // that, rather than being sent through sign-in for nothing.
        const probe = await probeHandoff(code);
        if (cancelled) return;
        if (probe && probe.status !== 'redeemable') {
          setPhase({ kind: 'unusable', reason: probe.status });
          return;
        }
        setPhase({ kind: 'signin' });
        return;
      }

      if (redeemed.current) return;
      redeemed.current = true;
      setPhase({ kind: 'redeeming' });
      const result = await redeemHandoff(code);
      if (cancelled) return;
      if (result.ok) {
        setPhase({ kind: 'ready', session: result.session });
        return;
      }
      if (result.code === 'HANDOFF_EXPIRED') {
        setPhase({ kind: 'unusable', reason: 'expired' });
      } else if (result.code === 'HANDOFF_ALREADY_REDEEMED') {
        setPhase({ kind: 'unusable', reason: 'consumed' });
      } else if (result.code === 'HANDOFF_NOT_FOUND') {
        setPhase({ kind: 'unusable', reason: 'unknown' });
      } else {
        setPhase({ kind: 'error', message: result.message });
      }
    }

    void begin();
    return () => {
      cancelled = true;
    };
  }, [code, authNonce]);

  if (phase.kind === 'checking' || phase.kind === 'redeeming') {
    return <div className="od-loading-shell">{t('giniHandoff.opening')}</div>;
  }

  if (phase.kind === 'signin') {
    // Reusing the existing Welcome gate keeps one sign-in surface. On
    // success we bump `authNonce`, which re-runs the effect above and
    // resumes redemption at the same URL.
    return <Welcome onSignedIn={() => setAuthNonce((n) => n + 1)} />;
  }

  if (phase.kind === 'unusable' || phase.kind === 'error') {
    const body =
      phase.kind === 'error'
        ? phase.message
        : phase.reason === 'expired'
          ? t('giniHandoff.expiredBody')
          : phase.reason === 'consumed'
            ? t('giniHandoff.consumedBody')
            : t('giniHandoff.unknownBody');
    return (
      <div className={styles.shell}>
        <div className={styles.page}>
          <div className={styles.card}>
            <h1 className={styles.title} data-testid="gini-handoff-unusable">
              {phase.kind === 'error' ? t('giniHandoff.errorTitle') : t('giniHandoff.unavailableTitle')}
            </h1>
            <p className={styles.subtitle}>{body}</p>
            <div className={styles.actions}>
              <Button type="button" variant="primary" onClick={onExit}>
                {t('giniHandoff.openStudio')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const session = phase.session;

  return (
    <div className={styles.shell}>
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>{session.projectTitle ?? session.project.name}</h1>
          <p className={styles.subtitle}>
            {session.project.created ? t('giniHandoff.projectCreated') : t('giniHandoff.projectOpened')}
          </p>
        </header>

        <HostedAccountPanel auth={auth} capabilities={session.capabilities} />

        <section className={styles.card} aria-labelledby="gini-handoff-brief">
          <h2 className={styles.cardTitle} id="gini-handoff-brief">
            {t('giniHandoff.briefHeading')}
          </h2>
          <p className={styles.brief} data-testid="gini-handoff-brief">
            {session.brief}
          </p>
          <div className={styles.metaGrid} data-testid="gini-handoff-source-context">
            <div>
              <span className={styles.metaLabel}>{t('giniHandoff.sourceConversation')}</span>
              <span className={styles.metaValue}>{session.sourceConversationId}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>{t('giniHandoff.sourceWorkspace')}</span>
              <span className={styles.metaValue}>{session.sourceWorkspaceId}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>{t('giniHandoff.projectHeading')}</span>
              <span className={styles.metaValue}>{session.project.studioProjectId}</span>
            </div>
          </div>
          <div className={styles.actions}>
            <Button type="button" variant="default" onClick={onExit} data-testid="gini-handoff-open-project">
              {t('giniHandoff.openProject')}
            </Button>
          </div>
        </section>

        <RunStatePanel run={session.run} />

        <ArtifactPanel
          handoffId={session.handoffId}
          pushDelivery={session.capabilities.artifactPushDelivery}
          onSelectionChange={setSelection}
        />

        <ReturnPanel handoffId={session.handoffId} inbox={session.capabilities.giniCodeInbox} selection={selection} />
      </div>
    </div>
  );
}
